<template>
    <transition
        name="popup"
        @enter="getHeight"
    >
        <article
            v-if="showPopup"
            ref="popup"
            class="popup-notification container"
            :class="[multiline]"
            :style="{...generateStylingVariables(content.style)}"
            @mouseover="freezePopup"
            @mouseleave="unfreezePopup"
        >
            <div
                class="popup-notification__inner"
                :class="checkChildMedia"
            >
                <zoe-atom-media
                    v-if="content.media.value"
                    ref="media"
                    class="popup-notification__media"
                    :item="content.media.value"
                    @load="onMediaLoaded"
                />

                <zoe-atom-text
                    v-if="stringIsNotEmpty(content.text.value)"
                    class="popup-notification__text"
                    :item="content.text.value"
                />
            </div>
        </article>
    </transition>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watchEffect} from 'vue';
import { useStore } from 'vuex';
import { componentProps } from '../../../../../mixins/component/component';
import { generateStylingVariables } from '../../../../../mixins/styling/styling';
import { stringIsNotEmpty } from '../../../../../../../resources/frontend/js/mixins/get-page-data';
import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeAtomMedia from '../../../../atoms/media/v1.0.0/zoe-atom-media.vue';

const store = useStore();

const props = defineProps({
    content: componentProps.content,
});

const popup = ref('popup');
const media = ref('media');
const durationBefore = ref(props.content.duration.before.value);
const durationDuring = ref(props.content.duration.during.value);
const mediaLoaded = ref(false);
const isTouchDevice = ref(false);
const showPopup = ref(false);
const hidePopup = ref(null);

const multiline = computed(() => {
    return getHeight();
});

const checkChildMedia = computed(() => {
    return mediaLoaded.value ? '' : 'popup-notification__inner--no-media';
});

const getHeight = () => {
    if ((mediaLoaded.value || !media.value) && showPopup.value) {
        nextTick(() => {
            const element = popup.value;
            const text = getDeepestChild(element);
            const textWrapper = element.querySelector('.popup-notification__text');

            const textHeight = text.getBoundingClientRect().height;
            const textStyle = window.getComputedStyle(text);
            const lineHeight = parseFloat(textStyle.lineHeight);

            const hasConditionalText = Array.from(textWrapper.children).some(child => child.className.includes('conditional'));

            if(textHeight > lineHeight || (textWrapper.children.length > 1 || hasConditionalText)) {
                return ('popup-notification--multiline');
            }
        });
    }
};

const getDeepestChild = (element) => {
    if (element.lastElementChild) {
        const child = getDeepestChild(element.lastElementChild);
        // Check if the deepest child is a text styling element
        if (['B', 'I', 'EM', 'STRONG', 'SPAN', 'S', 'U'].includes(child.tagName)) {
            // If it is, return the parent of that element
            return element;
        } else {
            return child;
        }
    } else {
        return element;
    }
};

const handlePopup = () => {
    setTimeout(() => {
        showPopup.value = true;

        hidePopup.value = setTimeout(() => {
            showPopup.value = false;
        }, durationDuring.value);

    }, durationBefore.value);
};

watchEffect(() => {
    if (store.state.page.step) {
        handlePopup()
    }
});

onMounted(() => {
    handlePopup();
    isTouchDevice.value = window.matchMedia('(pointer: coarse)').matches;
});

const onMediaLoaded = () => {
    mediaLoaded.value = true;
    getHeight();
};

const freezePopup = () => {
    if(!isTouchDevice.value) {
        clearTimeout(hidePopup.value);
    }
};

const unfreezePopup = () => {
    if(!isTouchDevice.value) {
        hidePopup.value = setTimeout(() => {
            showPopup.value = false;
        }, 1000);
    }
};
</script>

<style lang="scss" scoped>
@use 'zoe-organism-popup-notification';

@layer organism {
    .popup-notification {
        --background: v-bind('content.style.background.value');
    }

    .popup-enter-active,
    .popup-leave-active{
      transition: transform .6s cubic-bezier(.63, -.72, .2, 1.61);
    }

    .popup-leave-to,
    .popup-enter-from{
      transform: translateY(-150%);
    }
}
</style>
